/* =========================
    IMPORTS & CONFIGURATION
   ========================= */
import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { auth } from "../firebase";
import { collection, getDocs, doc, updateDoc, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { HelpCircle, Play, X, Eye, EyeOff } from "lucide-react";

// Video url converter
const getEmbedYoutubeUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
};

export default function StudentPortal({ activeTab, initialSubTab, setInitialSubTab }) {
  const { userData } = useAuth();
  
  /* ==================
      STATE MANAGEMENT
     ================== */
  
  /* Main Database Collections */
  const [materials, setMaterials] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [studentFees, setStudentFees] = useState([]);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [submissionsList, setSubmissionsList] = useState([]);
  const [announcementsList, setAnnouncementsList] = useState([]);

  /* Active Sub-tab Selector */
  const [subTab, setSubTab] = useState("");
  
  /* Read Notification Tracking */
  const [localReadIds, setLocalReadIds] = useState([]);

  /* Submissions Form Inputs */
  const [studentAnswerUrl, setStudentAnswerUrl] = useState({}); 
  const [uploading, setUploading] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({}); 
  const [structuredAnswers, setStructuredAnswers] = useState({}); 
  const [completedQuizzes, setCompletedQuizzes] = useState({}); 

  /* Video Player Modal States */
  const [activeVideoUrl, setActiveVideoUrl] = useState("");
  const [activeVideoType, setActiveVideoType] = useState(""); 
  const [activeVideoCourse, setActiveVideoCourse] = useState("");
  const [activeVideoBatch, setActiveVideoBatch] = useState("");

  /* Custom Dialog Alerts */
  const [customAlert, setCustomAlert] = useState(null); 
  const alertModalRef = useRef(null);

  /* Profile Management States */
  const [name, setName] = useState(userData?.fullName || "");
  const [phone, setPhone] = useState(userData?.phone || "");
  const [email, setEmail] = useState(userData?.email || "");
  const [address, setAddress] = useState(userData?.address || "");
  const [gender, setGender] = useState(userData?.gender || "Male");
  const [profilePic, setProfilePic] = useState(userData?.profilePic || "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordUpdating, setPasswordUpdating] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  
  // Triggers updates and syncs tabs whenever activeTab changes
  useEffect(() => {
    fetchStudentData();
    if (activeTab === "dashboard") {
      setSubTab("overview");
    } else if (activeTab === "materials") {
      setSubTab("lessons");
    } else if (activeTab === "fees") {
      setSubTab("balances");
    } else if (activeTab === "announcements") {
      if (initialSubTab) {
        setSubTab(initialSubTab);
        setTimeout(() => {
          if (setInitialSubTab) setInitialSubTab("");
        }, 50);
      } else {
        if (subTab !== "new" && subTab !== "old") {
          setSubTab("new");
        }
      }
    } else if (activeTab === "profile") {
      setSubTab("edit-profile");
    } else {
      setSubTab("");
    }
  }, [activeTab, initialSubTab]);

  useEffect(() => {
    if (userData?.readNotifications) {
      setLocalReadIds(userData.readNotifications);
    }
    if (userData) {
      setName(userData.fullName || "");
      setPhone(userData.phone || "");
      setEmail(userData.email || "");
      setAddress(userData.address || "");
      setGender(userData.gender || "Male");
      setProfilePic(userData.profilePic || "");
    }
  }, [userData]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (alertModalRef.current && !alertModalRef.current.contains(event.target)) {
        setCustomAlert(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ==========================
      DATABASE FETCH FUNCTIONS
     ========================== */
  async function fetchStudentData() {
    try {
      const mSnap = await getDocs(collection(db, "materials"));
      setMaterials(mSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      
      const cSnap = await getDocs(collection(db, "courses"));
      setCourses(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const bSnap = await getDocs(collection(db, "batches"));
      setBatches(bSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const fSnap = await getDocs(collection(db, "fees"));
      setStudentFees(fSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(f => 
        f.studentId?.toLowerCase().trim() === userData.customId?.toLowerCase().trim() ||
        f.studentId === userData.uid
      ));

      const aSnap = await getDocs(collection(db, "attendance"));
      setAttendanceLogs(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const asSnap = await getDocs(collection(db, "assessments"));
      setAssessments(asSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const subSnap = await getDocs(collection(db, "submissions"));
      setSubmissionsList(subSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.studentId === userData.uid));

      const annSnap = await getDocs(collection(db, "announcements"));
      setAnnouncementsList(annSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching student dataset: ", err);
    }
  }

  /* ========================
      SYSTEM ACTION HANDLERS
     ======================== */

  /* Profile Pic Upload handler */
  async function handleProfilePicUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setCustomAlert({ type: "error", message: "⚠️ File size exceeds 10MB limit." });
      return;
    }

    setUploading(true);
    try {
      const fileRef = ref(storage, `profiles/${userData.uid}-${Date.now()}-${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);
      setProfilePic(downloadUrl);
      setCustomAlert({ type: "success", message: "Profile picture uploaded successfully!" });
    } catch (err) {
      setCustomAlert({ type: "error", message: "Upload failed: " + err.message });
    }
    setUploading(false);
  }

  /* Assignment Document Upload */
  async function handleAssignmentFileUpload(e, examId) {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 500 * 1024 * 1024) {
      setCustomAlert({ type: "error", message: "⚠️ File size exceeds 500MB limit." });
      e.target.value = null;
      return;
    }

    setUploading(true);
    try {
      const fileRef = ref(storage, `submissions/${userData.uid}/${Date.now()}-${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);
      setStudentAnswerUrl({ ...studentAnswerUrl, [examId]: downloadUrl });
      setCustomAlert({ type: "success", message: "Project file uploaded successfully!" });
    } catch (err) {
      setCustomAlert({ type: "error", message: "File upload failed: " + err.message });
    }
    setUploading(false);
  }

  /* Student Submissions */
  async function handleFileExamSubmit(e, assessment) {
    e.preventDefault();
    const fileUrl = studentAnswerUrl[assessment.id];
    if (!fileUrl) {
      setCustomAlert({ type: "error", message: "Please upload your project file before submitting!" });
      return;
    }

    await addDoc(collection(db, "submissions"), {
      assessmentId: assessment.id,
      assessmentTitle: assessment.title,
      studentId: userData.uid,
      studentName: userData.fullName,
      studentAnswer: fileUrl, 
      score: 0,
      status: "pending",
      feedback: "Project file submitted.",
      courseCode: assessment.courseId,
      timestamp: new Date().toISOString()
    });

    setCompletedQuizzes({ ...completedQuizzes, [assessment.id]: "Submitted" });
    setCustomAlert({ type: "success", message: "Project submitted successfully!" });
    fetchStudentData();
  }

  async function handleMcqExamSubmit(e, assessment) {
    e.preventDefault();
    const studentChoices = selectedAnswers[assessment.id] || {};
    
    if (Object.keys(studentChoices).length < assessment.questions.length) {
      setCustomAlert({ type: "error", message: "Please answer all questions before submitting." });
      return;
    }

    let isAutoGraded = assessment.gradingType === "auto";
    let score = 0;
    let feedbackDetails = "";

    if (isAutoGraded) {
      let correctCount = 0;
      assessment.questions.forEach((q, idx) => {
        const studentOptionSelected = studentChoices[idx];
        const correctOptionText = q.options[parseInt(q.correctOptionNum) - 1];
        if (studentOptionSelected === correctOptionText) {
          correctCount++;
        }
      });
      score = Math.round((correctCount / assessment.questions.length) * 100);
      feedbackDetails = `System Automated: Evaluated score of ${score}% (Correct: ${correctCount}/${assessment.questions.length})`;
    } else {
      feedbackDetails = "MCQ grading requested.";
    }

    await addDoc(collection(db, "submissions"), {
      assessmentId: assessment.id,
      assessmentTitle: assessment.title,
      studentId: userData.uid,
      studentName: userData.fullName,
      studentAnswer: JSON.stringify(studentChoices),
      score: isAutoGraded ? score : 0,
      status: isAutoGraded ? "graded" : "pending",
      feedback: feedbackDetails,
      courseCode: assessment.courseId,
      timestamp: new Date().toISOString()
    });

    setCompletedQuizzes({ ...completedQuizzes, [assessment.id]: isAutoGraded ? score : "Submitted" });
    setCustomAlert({ type: "success", message: isAutoGraded ? `Exam Submitted! Instant Score: ${score}%` : "Exam submitted successfully!" });
    fetchStudentData();
  }

  async function handleStructuredExamSubmit(e, assessment) {
    e.preventDefault();
    const studentDrafts = structuredAnswers[assessment.id] || {};

    if (Object.keys(studentDrafts).length < assessment.questions.length) {
      setCustomAlert({ type: "error", message: "Please draft answers for all questions before submitting." });
      return;
    }

    await addDoc(collection(db, "submissions"), {
      assessmentId: assessment.id,
      assessmentTitle: assessment.title,
      studentId: userData.uid,
      studentName: userData.fullName,
      studentAnswer: JSON.stringify(studentDrafts), 
      score: 0,
      status: "pending",
      feedback: "Structured assessment submitted.",
      courseCode: assessment.courseId,
      timestamp: new Date().toISOString()
    });

    setCompletedQuizzes({ ...completedQuizzes, [assessment.id]: "Submitted" });
    setCustomAlert({ type: "success", message: "Structured assessment submitted successfully!" });
    fetchStudentData();
  }

  /* Modify Account Profile */
  async function handleProfileUpdate(e) {
    e.preventDefault();
    const oldName = userData.fullName;
    const oldPhone = userData.phone;

    await updateDoc(doc(db, "users", userData.uid), {
      fullName: name,
      phone: phone,
      address: address,
      gender: gender,
      profilePic: profilePic
    });

    await addDoc(collection(db, "auditLogs"), {
      action: "Profile Modification",
      details: `${userData.customId} modified profile parameters in system.`,
      timestamp: new Date().toISOString()
    });

    setCustomAlert({ type: "success", message: "Profile updated successfully." });
  }

  async function handlePasswordUpdate(e) {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmPassword) return;

    if (newPassword !== confirmPassword) {
      setCustomAlert({ type: "error", message: "⚠️ Confirmation Error: New passwords do not match!" });
      return;
    }

    // Password Strength Validation Guard: Min 6, Max 15, 1 Capital letter, 1 Special Symbol
    if (newPassword.length < 6 || newPassword.length > 15) {
      setCustomAlert({ type: "error", message: "⚠️ Password must be between 6 and 15 characters long." });
      return;
    }

    const hasCapitalLetter = /[A-Z]/.test(newPassword);
    const hasSpecialSymbol = /[\W_]/.test(newPassword);

    if (!hasCapitalLetter) {
      setCustomAlert({ type: "error", message: "⚠️ Password must contain at least one capital letter." });
      return;
    }

    if (!hasSpecialSymbol) {
      setCustomAlert({ type: "error", message: "⚠️ Password must contain at least one special symbol (e.g. @, #, $, %)." });
      return;
    }

    setPasswordUpdating(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, oldPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      await addDoc(collection(db, "auditLogs"), {
        action: "Password Change Logged",
        details: `${userData.customId} modified account password successfully.`,
        timestamp: new Date().toISOString()
      });

      setOldPassword("");
      setNewPassword("");
      setConfirmPassword(""); 
      setCustomAlert({ type: "success", message: "Password updated successfully!" });
    } catch (err) {
      setCustomAlert({ type: "error", message: "Re authentication failed: " + err.message });
    }
    setPasswordUpdating(false);
  }

  /* Mark Announcements Read */
  async function markAsRead(id) {
    if (!localReadIds.includes(id)) {
      const updatedReads = [...localReadIds, id];
      setLocalReadIds(updatedReads); 

      await updateDoc(doc(db, "users", userData.uid), {
        readNotifications: updatedReads
      });
      fetchStudentData();
    }
  }

  /* ====================================
      IN-MEMORY FILTER PRESETS & LOOKUPS
     ==================================== */
  
  const getCourseNameByCode = (code) => {
    const match = courses.find(c => c.code === code);
    return match ? match.name : code;
  };

  const calculateCourseBatchAttendance = (batchId) => {
    let totalSessions = 0;
    let presentSessions = 0;

    attendanceLogs.forEach(sheet => {
      if (sheet.batchId === batchId && sheet.records && sheet.records[userData.uid] !== undefined) {
        totalSessions++;
        if (sheet.records[userData.uid] === "Present") {
          presentSessions++;
        }
      }
    });

    if (totalSessions === 0) return "No Logs";
    return `${((presentSessions / totalSessions) * 100).toFixed(1)}%`;
  };

  const calculateAttendancePercentage = () => {
    let totalSessions = 0;
    let presentSessions = 0;

    attendanceLogs.forEach(sheet => {
      if (sheet.records && sheet.records[userData.uid] !== undefined) {
        totalSessions++;
        if (sheet.records[userData.uid] === "Present") {
          presentSessions++;
        }
      }
    });

    if (totalSessions === 0) return "0.0%";
    return `${((presentSessions / totalSessions) * 100).toFixed(1)}%`;
  };

  const myEnrolledCourseCodes = (userData.enrollments || []).map(en => en.courseCode);
  const myEnrolledBatches = (userData.enrollments || []).map(en => en.batchName);

  const filteredMaterials = materials.filter(m => {
    return myEnrolledCourseCodes.includes(m.courseId) && (m.batchId === "all" || myEnrolledBatches.includes(m.batchId));
  });

  const activeFilteredExams = assessments.filter(exam => {
    const isEnrolled = myEnrolledCourseCodes.includes(exam.courseId) && myEnrolledBatches.includes(exam.batchName);
    const isDeadlinePassed = new Date().getTime() > new Date(exam.deadline).getTime();
    return isEnrolled && !isDeadlinePassed; 
  });

  const getCourseFeeStatusAndBalance = (courseCode, batchName) => {
    const batchObj = batches.find(b => b.batchName === batchName);
    const courseMainFee = batchObj ? parseFloat(batchObj.courseFee || 0) : 0;

    const coursePayments = studentFees.filter(f => f.courseCode === courseCode && f.batchName === batchName && f.status === "Paid");
    const totalPaid = coursePayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const outstanding = courseMainFee - totalPaid;

    if (totalPaid >= courseMainFee) {
      return { status: "Paid", balance: 0, mainFee: courseMainFee };
    } else {
      return { status: "Outstanding", balance: outstanding, mainFee: courseMainFee };
    }
  };

  const myAnnouncements = announcementsList.filter(n => {
    const isGlobal = n.targetType === "all" || n.targetRole === "student";
    const isTargetedCourse = myEnrolledCourseCodes.includes(n.targetCourseId);
    return isGlobal || isTargetedCourse;
  });

  const sortedAnnouncements = [...myAnnouncements].sort((a, b) => {
    return new Date(b.timestamp || b.date).getTime() - new Date(a.timestamp || a.date).getTime();
  });

  const outstandingFeesBalance = (userData.enrollments || []).reduce((acc, en) => {
    const feeObj = getCourseFeeStatusAndBalance(en.courseCode, en.batchName);
    return acc + feeObj.balance;
  }, 0);

  const sortedAttendanceLogs = [...attendanceLogs].sort((a, b) => {
    if (a.date !== b.date) {
      return new Date(a.date) - new Date(b.date); 
    }
    return a.batchId?.localeCompare(b.batchId); 
  });

  const getDummyAvatar = () => {
    if (gender === "Female") {
      return "https://cdn-icons-png.flaticon.com/512/3135/3135768.png";
    }
    return "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";
  };

  // Navigations styling helper
  const subTabStyle = (isActive) => ({
    padding: "0.5rem 1rem",
    fontSize: "0.85rem",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    background: isActive ? "var(--navy-primary)" : "transparent",
    color: isActive ? "#ffffff" : "var(--text-muted)"
  });

  return (
    <div>
      {/* Video Player Modal */}
      {activeVideoUrl && (
        <div className="card" style={{ border: "2px solid var(--navy-primary)", padding: "1.5rem", position: "relative", marginBottom: "2rem" }}>
          
          <button 
            onClick={() => { setActiveVideoUrl(""); setActiveVideoType(""); }}
            style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}
          >
            <X size={20} />
          </button>
          
          <h4 style={{ marginBottom: "0.25rem" }}>Playing Video:</h4>
          <p style={{ color: "var(--text-muted)", marginBottom: "1rem", fontSize: "0.9rem" }}>
            Course: <strong>{activeVideoCourse}</strong> | Batch Group: <strong>{activeVideoBatch}</strong>
          </p>
          
          <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: "8px", background: "#000" }}>
            {activeVideoType === "youtube" ? (
              <iframe 
                src={getEmbedYoutubeUrl(activeVideoUrl)} 
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }} 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen 
                title="Class Video"
              />
            ) : (
              <video 
                src={activeVideoUrl} 
                controls 
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              />
            )}
          </div>

          {activeVideoType === "youtube" && (
            <div style={{ marginTop: "1rem", textAlign: "center" }}>
              <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                Having trouble playing inline? Some videos are restricted by YouTube.
              </p>
              <a 
                href={activeVideoUrl} 
                target="_blank" 
                rel="noreferrer" 
                className="navy-btn" 
                style={{ fontSize: "0.85rem", padding: "0.5rem 1.25rem", background: "var(--navy-light)", textDecoration: "none", display: "inline-flex" }}
              >
                Open Directly on YouTube ↗
              </a>
            </div>
          )}

        </div>
      )}
      
      {/* =========================
           PORTAL SUB-SECTION TABS 
          ========================= */}
      <div className="sub-sections-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "1rem", marginBottom: "1.5rem" }}>
        <div className="sub-tabs-container" style={{ display: "flex", gap: "0.5rem" }}>
          
          {activeTab === "dashboard" && (
            <>
              <button className={`sub-tab-btn ${subTab === "overview" ? "active" : ""}`} onClick={() => setSubTab("overview")} style={subTabStyle(subTab === "overview")}>Overview</button>
              <button className={`sub-tab-btn ${subTab === "attendance" ? "active" : ""}`} onClick={() => setSubTab("attendance")} style={subTabStyle(subTab === "attendance")}>Attendance logs</button>
            </>
          )}

          {activeTab === "materials" && (
            <>
              <button className={`sub-tab-btn ${subTab === "lessons" ? "active" : ""}`} onClick={() => setSubTab("lessons")} style={subTabStyle(subTab === "lessons")}>Lessons</button>
              <button className={`sub-tab-btn ${subTab === "quizzes" ? "active" : ""}`} onClick={() => setSubTab("quizzes")} style={subTabStyle(subTab === "quizzes")}>Active Assessments</button>
              <button className={`sub-tab-btn ${subTab === "performance" ? "active" : ""}`} onClick={() => setSubTab("performance")} style={subTabStyle(subTab === "performance")}>Performance Reports</button>
            </>
          )}

          {activeTab === "fees" && (
            <>
              <button className={`sub-tab-btn ${subTab === "balances" ? "active" : ""}`} onClick={() => setSubTab("balances")} style={subTabStyle(subTab === "balances")}>Balances</button>
              <button className={`sub-tab-btn ${subTab === "payments-history" ? "active" : ""}`} onClick={() => setSubTab("payments-history")} style={subTabStyle(subTab === "payments-history")}>Payments History</button>
            </>
          )}

          {activeTab === "announcements" && (
            <>
              <button className={`sub-tab-btn ${subTab === "new" ? "active" : ""}`} onClick={() => setSubTab("new")} style={subTabStyle(subTab === "new")}>New Announcements</button>
              <button className={`sub-tab-btn ${subTab === "old" ? "active" : ""}`} onClick={() => setSubTab("old")} style={subTabStyle(subTab === "old")}>Read Announcements</button>
            </>
          )}

          {activeTab === "profile" && (
            <>
              <button className={`sub-tab-btn ${subTab === "edit-profile" ? "active" : ""}`} onClick={() => setSubTab("edit-profile")} style={subTabStyle(subTab === "edit-profile")}>Manage Profile</button>
              <button className={`sub-tab-btn ${subTab === "security" ? "active" : ""}`} onClick={() => setSubTab("security")} style={subTabStyle(subTab === "security")}>Change Password</button>
            </>
          )}

        </div>
      </div>

      {/* ===========================
           PORTAL DATA VIEWS & FORMS
          =========================== */}

      {/* Dashboard Tab Views */}
      {activeTab === "dashboard" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "overview" && (
            <>
              <div className="grid-matrix">
                <div className="card" style={{ textAlign: "center", borderTop: "4px solid var(--warning)", padding: "1.5rem" }}>
                  <h3 style={{ fontSize: "1.1rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Total Outstanding Fees</h3>
                  <p style={{ fontSize: "2rem", fontWeight: "bold", color: "var(--warning)", margin: 0 }}>LKR {outstandingFeesBalance.toLocaleString()}</p>
                </div>
                <div className="card" style={{ textAlign: "center", borderTop: "4px solid var(--success)", padding: "1.5rem" }}>
                  <h3 style={{ fontSize: "1.1rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Enrolled Modules</h3>
                  <p style={{ fontSize: "2rem", fontWeight: "bold", color: "var(--success)", margin: 0 }}>{(userData?.enrollments || []).length} Active</p>
                </div>
              </div>

              <div className="card">
                <h3 className="card-title">My Courses & Attendance Rates</h3>
                <div className="table-container">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>Course Name & ID</th>
                        <th>Batch</th>
                        <th>Attendance Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(userData?.enrollments || []).map((en, index) => (
                        <tr key={index}>
                          <td><strong>{getCourseNameByCode(en.courseCode)}</strong> <small style={{ color: "var(--text-muted)" }}>({en.courseCode})</small></td>
                          <td><span className="badge badge-approved">{en.batchName}</span></td>
                          <td><strong>{calculateCourseBatchAttendance(en.batchName)}</strong></td>
                        </tr>
                      ))}
                      {(userData?.enrollments || []).length === 0 && (
                        <tr style={{ textAlign: "center", color: "var(--text-muted)" }}>
                          <td colSpan="3" style={{ padding: "1.5rem" }}>No enrollments assigned yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {subTab === "attendance" && (
            <div className="card">
              <h3 className="card-title">My Detailed Attendance Logs</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Class Date</th>
                      <th>Course Code & Name</th>
                      <th>Course Batch</th>
                      <th>Attendance Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAttendanceLogs.filter(sheet => myEnrolledBatches.includes(sheet.batchId)).map(sheet => {
                      const status = sheet.records?.[userData.uid] || "Not Marked";
                      const batchObj = batches.find(b => b.batchName === sheet.batchId);
                      const courseDocId = batchObj ? batchObj.courseId : "";
                      const courseObj = courses.find(c => c.id === courseDocId);
                      const courseDisplay = courseObj ? `${courseObj.code} - ${courseObj.name}` : "N/A";

                      return (
                        <tr key={sheet.id}>
                          <td><strong>{sheet.date}</strong></td>
                          <td><strong>{courseDisplay}</strong></td>
                          <td><span className="badge badge-approved" style={{ background: "#cbd5e1", color: "#1e293b" }}>{sheet.batchId}</span></td>
                          <td>
                            <span className={`badge ${status === "Present" ? "badge-approved" : "badge-pending"}`} style={{ background: status === "Absent" ? "#fee2e2" : undefined, color: status === "Absent" ? "#991b1b" : undefined }}>
                              {status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Materials & Resource Views */}
      {activeTab === "materials" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2.5rem" }}>
          
          {subTab === "lessons" && (
            <div className="card">
              <h3 className="card-title">Download Course Materials</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Course Name</th>
                      <th>Download / Play</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMaterials.map(m => (
                      <tr key={m.id}>
                        <td>{m.title}</td>
                        <td><strong>{getCourseNameByCode(m.courseId)}</strong> ({m.batchId})</td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            {m.link && <a href={m.link} target="_blank" rel="noreferrer" className="navy-btn" style={{ textDecoration: "none", fontSize: "0.85rem", padding: "0.4rem 0.8rem" }}>Download File</a>}
                            {m.link && m.link.endsWith(".mp4") && (
                              <button className="navy-btn" style={{ padding: "0.4rem 0.8rem", background: "var(--success)" }} onClick={() => { setActiveVideoUrl(m.link); setActiveVideoType("mp4"); setActiveVideoCourse(getCourseNameByCode(m.courseId)); setActiveVideoBatch(m.batchId); }}><Play size={14}/> Play Video</button>
                            )}
                            {m.youtubeLink && (
                              <button className="navy-btn" style={{ padding: "0.4rem 0.8rem", background: "var(--danger)" }} onClick={() => { setActiveVideoUrl(m.youtubeLink); setActiveVideoType("youtube"); setActiveVideoCourse(getCourseNameByCode(m.courseId)); setActiveVideoBatch(m.batchId); }}><Play size={14}/> Play Video</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredMaterials.length === 0 && (
                      <tr>
                        <td colSpan="3" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No shared class materials.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subTab === "quizzes" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-light)" }}>
              <h3 className="card-title" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <HelpCircle size={20} color="var(--navy-light)" />
                Active Assessments
              </h3>
              {activeFilteredExams.map(exam => {
                const hasSubmittedAlready = submissionsList.some(s => s.assessmentId === exam.id) || completedQuizzes[exam.id];
                const totalExamMarks = parseFloat(exam.totalMarks || 100);

                return (
                  <div key={exam.id} className="card" style={{ backgroundColor: "#f8fafc", borderLeft: "5px solid var(--navy-primary)", marginBottom: "2rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
                      <div>
                        <h4 style={{ color: "var(--navy-primary)", fontSize: "1.1rem" }}>{exam.title}</h4>
                        <small style={{ color: "var(--text-muted)" }}>Course: <strong>{getCourseNameByCode(exam.courseId)}</strong> | Batch: <strong>{exam.batchName}</strong> | Type: <strong>{exam.assessmentType?.toUpperCase()}</strong></small>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span className="badge badge-approved" style={{ background: "var(--navy-primary)", color: "white", marginRight: "0.5rem" }}>Total Marks: {totalExamMarks}</span>
                        <span className="badge badge-pending" style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                          Deadline: {new Date(exam.deadline).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {hasSubmittedAlready ? (
                      <div style={{ padding: "1rem", backgroundColor: "#d1fae5", borderRadius: "8px", color: "#065f46", fontWeight: "bold" }}>
                        ✓ Assessment Answer Submitted Successfully.
                      </div>
                    ) : (
                      <>
                        {exam.assessmentType === "file" ? (
                          <form onSubmit={(e) => handleFileExamSubmit(e, exam)}>
                            <div className="form-group">
                              <p style={{ fontWeight: "600", marginBottom: "1rem" }}>{exam.questions?.[0]?.questionText}</p>
                              <label>Upload Assignment Project File (ZIP, MP4, PDF, DOCX up to 500MB)</label>
                              <input type="file" className="form-control" onChange={(e) => handleAssignmentFileUpload(e, exam.id)} required />
                              {uploading && <small style={{ color: "var(--success)", display: "block", marginTop: "0.5rem" }}>Uploading file...</small>}
                            </div>
                            <button type="submit" className="navy-btn" disabled={!studentAnswerUrl[exam.id] || uploading}>Submit Project</button>
                          </form>
                        ) : (
                          <form onSubmit={(e) => {
                            if (exam.assessmentType === "mcq") {
                              handleMcqExamSubmit(e, exam);
                            } else {
                              handleStructuredExamSubmit(e, exam);
                            }
                          }}>
                            {exam.questions && exam.questions.map((q, qIdx) => {
                              const qMarks = parseFloat(q.marks || 0);
                              const weightage = totalExamMarks > 0 ? ((qMarks / totalExamMarks) * 100).toFixed(1) : "0.0";

                              return (
                                <div key={qIdx} style={{ marginBottom: "1.5rem", borderBottom: "1px dashed var(--border-color)", paddingBottom: "1.25rem" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                                    <p style={{ fontWeight: "600", margin: 0 }}>Q{qIdx + 1}. {q.questionText}</p>
                                    <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontWeight: "bold" }}>Marks: {qMarks} | Percentage: {weightage}% of total</span>
                                  </div>
                                  
                                  {exam.assessmentType === "mcq" && q.options && q.options.map((opt, oIdx) => (
                                    <label key={oIdx} style={{ display: "block", marginBottom: "0.5rem", cursor: "pointer", paddingLeft: "0.5rem" }}>
                                      <input 
                                        type="radio" 
                                        name={`quiz-${exam.id}-q-${qIdx}`} 
                                        value={opt}
                                        checked={selectedAnswers[exam.id]?.[qIdx] === opt}
                                        onChange={(e) => {
                                          const quizAns = selectedAnswers[exam.id] || {};
                                          setSelectedAnswers({
                                            ...selectedAnswers,
                                            [exam.id]: { ...quizAns, [qIdx]: e.target.value }
                                          });
                                        }}
                                        required
                                        style={{ marginRight: "0.5rem" }}
                                      />
                                      {opt}
                                    </label>
                                  ))}

                                  {exam.assessmentType === "structured" && (
                                    <textarea 
                                      className="form-control" 
                                      rows="3"
                                      value={structuredAnswers[exam.id]?.[qIdx] || ""}
                                      onChange={(e) => {
                                        const structAns = structuredAnswers[exam.id] || {};
                                        setStructuredAnswers({
                                          ...structuredAnswers,
                                          [exam.id]: { ...structAns, [qIdx]: e.target.value }
                                        });
                                      }}
                                      required
                                    />
                                  )}
                                </div>
                              );
                            })}
                            <button type="submit" className="navy-btn">Submit Assessment</button>
                          </form>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              {activeFilteredExams.length === 0 && (
                <p style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No active assessments.</p>
              )}
            </div>
          )}

          {subTab === "performance" && (
            <div className="card">
              <h3 className="card-title">Grade Reports</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Course Name</th>
                      <th>Grades Score</th>
                      <th>Review Feedback</th>
                      <th>Review Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissionsList.map(res => (
                      <tr key={res.id}>
                        <td><strong>{res.assessmentTitle || "N/A"}</strong></td>
                        <td><strong>{getCourseNameByCode(res.courseCode)}</strong></td>
                        <td><strong>{res.score} / 100</strong></td>
                        <td>{res.feedback || "Awaiting instructor manual review"}</td>
                        <td>
                          <span className={`badge badge-${res.status === "graded" ? "approved" : "pending"}`}>
                            {res.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Banking Gateway */}
      {activeTab === "fees" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "balances" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">Outstanding Balances</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Batch</th>
                      <th>Course Fee</th>
                      <th>Outstanding Balance</th>
                      <th>Payment Status</th>
                      <th>Bank Account details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(userData.enrollments || []).map((en, index) => {
                      const c = courses.find(course => course.code === en.courseCode);
                      const b = batches.find(batch => batch.batchName === en.batchName);
                      
                      if (!c) return null; 

                      const feeObj = getCourseFeeStatusAndBalance(en.courseCode, en.batchName);

                      return (
                        <tr key={index}>
                          <td><strong>{c.name} ({c.code})</strong></td>
                          <td><span className="badge badge-approved" style={{ background: "#cbd5e1", color: "#1e293b" }}>{en.batchName}</span></td>
                          <td>LKR {feeObj.mainFee?.toLocaleString()}</td>
                          <td style={{ color: feeObj.balance > 0 ? "var(--danger)" : "var(--success)", fontWeight: "bold" }}>LKR {feeObj.balance?.toLocaleString()}</td>
                          <td>
                            <span className={`badge badge-${feeObj.status === "Paid" ? "approved" : "pending"}`}>
                              {feeObj.status}
                            </span>
                          </td>
                          <td>
                            {b && b.bankName && b.bankName !== "N/A" && b.bankName !== "Pending Configuration" ? (
                              <>
                                <small><strong>Name: {b.accountHolder}</strong></small><br/>
                                <small><strong>Acc No: {b.accountNumber}</strong></small><br/>
                                <small><strong>{b.bankName}</strong></small><br/>
                                <small><strong>{b.bankBranch}</strong></small>
                              </>
                            ) : (
                              <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Pending Bank Setup</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {(userData.enrollments || []).length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No active batch billing invoices.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {subTab === "payments-history" && (
            <div className="card">
              <h3 className="card-title">My Payment History</h3>
              <div className="table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Batch</th>
                      <th>Settled Amount</th>
                      <th>Paid Date</th>
                      <th>Settle Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentFees.filter(f => f.status === "Paid").map(f => (
                      <tr key={f.id}>
                        <td><strong>{f.courseName}</strong> ({f.courseCode})</td>
                        <td><span className="badge badge-approved" style={{ background: "#f1f5f9", color: "#334155" }}>{f.batchName || "N/A"}</span></td>
                        <td>LKR {f.amount?.toLocaleString()}</td>
                        <td>{f.paidDate || "N/A"}</td>
                        <td><span className="badge badge-approved">{f.paymentMethod || "Cash"}</span></td>
                      </tr>
                    ))}
                    {studentFees.filter(f => f.status === "Paid").length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No tuition payments settled yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* Announcements */}
      {activeTab === "announcements" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "new" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">New Announcements</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", marginTop: "1rem" }}>
                {sortedAnnouncements.filter(ann => !localReadIds.includes(ann.id)).map(ann => (
                  <div key={ann.id} style={{ background: "#f1f5f9", borderLeft: "5px solid var(--navy-primary)", padding: "1.5rem", borderRadius: "8px", position: "relative" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem" }}>
                      <h4 style={{ color: "var(--navy-primary)", margin: 0 }}>{ann.title}</h4>
                      <button 
                        className="navy-btn" 
                        onClick={() => markAsRead(ann.id)}
                        style={{ padding: "0.35rem 0.75rem", fontSize: "0.8rem", background: "var(--success)" }}
                      >
                        Mark as Read
                      </button>
                    </div>
                    <p style={{ lineHeight: "1.6", color: "var(--text-main)" }}>{ann.content}</p>
                    <small style={{ color: "var(--text-muted)", display: "block", marginTop: "0.75rem" }}>
                      Published: {new Date(ann.timestamp || ann.date).toLocaleString()}
                    </small>
                  </div>
                ))}
                {sortedAnnouncements.filter(ann => !localReadIds.includes(ann.id)).length === 0 && (
                  <p style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No new unread announcements.</p>
                )}
              </div>
            </div>
          )}

          {subTab === "old" && (
            <div className="card">
              <h3 className="card-title">Read Announcements</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", marginTop: "1rem" }}>
                {sortedAnnouncements.filter(ann => localReadIds.includes(ann.id)).map(ann => (
                  <div key={ann.id} style={{ background: "var(--white)", borderLeft: "5px solid #cbd5e1", padding: "1.5rem", borderRadius: "8px", position: "relative", border: "1px solid #cbd5e1" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem" }}>
                      <h4 style={{ color: "var(--navy-primary)", margin: 0 }}>{ann.title}</h4>
                      <span className="badge badge-approved" style={{ background: "#d1fae5", color: "#065f46" }}>Read</span>
                    </div>
                    <p style={{ lineHeight: "1.6", color: "var(--text-main)" }}>{ann.content}</p>
                    <small style={{ color: "var(--text-muted)", display: "block", marginTop: "0.75rem" }}>
                      Published: {new Date(ann.timestamp || ann.date).toLocaleString()}
                    </small>
                  </div>
                ))}
                {sortedAnnouncements.filter(ann => localReadIds.includes(ann.id)).length === 0 && (
                  <p style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No read announcements found.</p>
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {/* Profile Settings */}
      {activeTab === "profile" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          
          {subTab === "edit-profile" && (
            <div className="card" style={{ borderTop: "4px solid var(--navy-primary)" }}>
              <h3 className="card-title">Manage Profile</h3>
              
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", marginBottom: "2rem" }}>
                <img 
                  src={profilePic || getDummyAvatar()} 
                  alt="Profile" 
                  style={{ width: "120px", height: "120px", borderRadius: "50%", objectFit: "cover", border: "3px solid var(--navy-primary)" }} 
                />
                
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <label className="navy-btn" style={{ cursor: "pointer", fontSize: "0.85rem", padding: "0.5rem 1rem", textAlign: "center" }}>
                    {uploading ? "Uploading..." : "Upload New Photo"}
                    <input type="file" onChange={handleProfilePicUpload} style={{ display: "none" }} disabled={uploading} />
                  </label>
                  {profilePic && (
                    <button 
                      type="button" 
                      onClick={() => setProfilePic("")} 
                      className="navy-btn navy-btn-danger" 
                      style={{ fontSize: "0.85rem", padding: "0.4rem 1rem" }}
                    >
                      Remove Photo
                    </button>
                  )}
                </div>
              </div>

              <form onSubmit={handleProfileUpdate}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                
                <div className="form-group">
                  <label>Email Address</label>
                  <input type="email" className="form-control" value={email} disabled style={{ backgroundColor: "#e2e8f0", color: "#64748b", cursor: "not-allowed" }} />
                </div>
                
                <div className="form-group">
                  <label>Phone Number</label>
                  <input type="text" className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} required />
                </div>
                
                <div className="form-group">
                  <label>Address (Optional)</label>
                  <textarea 
                    className="form-control" 
                    rows="3" 
                    value={address} 
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </div>

                <div className="form-group" style={{ maxWidth: "200px" }}>
                  <label>Gender</label>
                  <select className="form-control" value={gender} onChange={(e) => setGender(e.target.value)}>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <button type="submit" className="navy-btn">Commit Changes</button>
              </form>
            </div>
          )}

          {subTab === "security" && (
        <div className="card" style={{ borderTop: "4px solid var(--danger)" }}>
          <h3 className="card-title" style={{ color: "var(--danger)" }}>Update Password Security</h3>
          <form onSubmit={handlePasswordUpdate}>
            
            <div className="form-group">
              <label>Enter Old Password</label>
              <div style={{ position: "relative" }}>
                <input 
                  type={showPassword ? "text" : "password"} 
                  className="form-control" 
                  value={oldPassword} 
                  onChange={(e) => setOldPassword(e.target.value)} 
                  required 
                  style={{ paddingRight: "2.5rem" }}
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center" }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Enter New Password</label>
              <div style={{ position: "relative" }}>
                <input 
                  type={showPassword ? "text" : "password"} 
                  className="form-control" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  required 
                  style={{ paddingRight: "2.5rem" }}
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center" }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: "2rem" }}>
              <label>Confirm New Password</label>
              <div style={{ position: "relative" }}>
                <input 
                  type={showPassword ? "text" : "password"} 
                  className="form-control" 
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  required 
                />              
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center" }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
              </div>
            </div>

            <button type="submit" className="navy-btn navy-btn-danger" disabled={passwordUpdating}>
              {passwordUpdating ? "Validating " : "Update Password"}
            </button>
          </form>
        </div>
          )}

        </div>
      )}

      {/* Popups & Alerts Dialogs */}
      {customAlert && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" ref={alertModalRef} style={{ maxWidth: "450px", width: "100%", padding: "2rem", textAlign: "center", position: "relative" }}>
            <button onClick={() => setCustomAlert(null)} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 style={{ color: customAlert.type === "success" ? "var(--success)" : "var(--danger)", marginBottom: "1rem" }}>
              {customAlert.type === "success" ? "Success!" : "Notice / Error"}
            </h3>
            <p style={{ color: "var(--text-main)", marginBottom: "1.5rem" }}>{customAlert.message}</p>
            <button className="navy-btn" onClick={() => setCustomAlert(null)} style={{ margin: "0 auto" }}>Close Alert</button>
          </div>
        </div>
      )}

    </div>
  );
}